// ============================================================
// CarePeopleRoleAssignModal.jsx  (2b-13 Gate 5)
// Modal form to assign / change a user's CarePeople module role.
//
// Presentational + locally-stateful: owns the role/tier selection;
// the parent (<CarePeopleRolePanel>) owns the submit + async state.
//
//   - Radio: HR / Supervisor / HR Manager — filtered by `allowedRoles`
//     (per-role core-role eligibility, role-model expansion §6.3
//     2026-07-13: admin → all three; auditor → hr/supervisor;
//     manager → hr_manager only). The server re-validates regardless.
//   - Tier select with helper text:
//       Tier 0 — carer records only
//       Tier 1 — carer + supervisor records
//       Tier 2 — carer + supervisor + manager records  (HR only)
//   - Supervisor caps at tier 1 (A1 §3): the tier-2 option is
//     disabled, and flipping to Supervisor while tier 2 is selected
//     clamps the selection back to 1.
//   - Inline error (from the parent's failed submit) keeps the modal
//     open so the admin can correct + retry.
// ============================================================

import React, { useEffect, useState } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';
import Modal from './Modal';
import './CarePeopleRoleAssignModal.css';

const TIER_HELP = {
  0: 'Carer records only.',
  1: 'Carer + supervisor records.',
  2: 'Carer + supervisor + manager records.',
};

const ROLE_OPTIONS = [
  { value: 'hr', label: 'HR' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'hr_manager', label: 'HR Manager' },
];

export default function CarePeopleRoleAssignModal({
  isOpen,
  onClose,
  onSubmit,
  submitting = false,
  errorMessage = null,
  initialRole = 'hr',
  initialTier = 0,
  allowedRoles = ['hr', 'supervisor', 'hr_manager'],
}) {
  const [role, setRole] = useState(initialRole);
  const [tier, setTier] = useState(initialTier);

  // Reset to the seeded values each time the modal opens (so "Change
  // role" pre-fills from the current grant, and a re-open after a
  // cancel starts clean).
  useEffect(() => {
    if (isOpen) {
      // Clamp into the allowed set (e.g. a manager-core user's only option
      // is hr_manager — the 'hr' default must not leak into the submit).
      setRole(allowedRoles.includes(initialRole) ? initialRole : allowedRoles[0]);
      setTier(initialTier);
    }
    // allowedRoles deliberately NOT a dependency: it is a fresh array each
    // render; depending on it would reset the selection on every re-render
    // while open. It only matters at open time.
  }, [isOpen, initialRole, initialTier]);

  // A1 §3: Supervisor tier is capped at 1. If the admin flips to
  // Supervisor while tier 2 is selected, clamp to 1.
  useEffect(() => {
    if (role === 'supervisor' && tier === 2) setTier(1);
  }, [role, tier]);

  function handleSubmit(e) {
    e.preventDefault();
    if (submitting) return;
    onSubmit({ carePeopleRole: role, roleScopeTier: tier });
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Assign CarePeople role" closeDisabled={submitting}>
      <form className="cp-role-modal" onSubmit={handleSubmit}>
        <fieldset className="cp-role-modal__fieldset" disabled={submitting}>
          <legend className="cp-role-modal__legend">Role</legend>
          {ROLE_OPTIONS.filter((o) => allowedRoles.includes(o.value)).map((o) => (
            <label className="cp-role-modal__radio" key={o.value}>
              <input
                type="radio"
                name="carePeopleRole"
                value={o.value}
                checked={role === o.value}
                onChange={() => setRole(o.value)}
              />
              <span>{o.label}</span>
            </label>
          ))}
          {allowedRoles.includes('hr_manager') && (
            <span className="cp-role-modal__help">
              HR Manager = everything HR can do, plus CarePeople settings (supervision interval, appraisal mode, leave settings).
            </span>
          )}
        </fieldset>

        <label className="cp-role-modal__field">
          <span className="cp-role-modal__label">Scope tier</span>
          <select
            className="cp-role-modal__select"
            value={tier}
            onChange={(e) => setTier(Number(e.target.value))}
            disabled={submitting}
            data-testid="cp-role-tier-select"
          >
            <option value={0}>Tier 0 — carer records only</option>
            <option value={1}>Tier 1 — carer + supervisor records</option>
            <option value={2} disabled={role === 'supervisor'}>
              Tier 2 — carer + supervisor + manager records{role === 'supervisor' ? ' (HR only)' : ''}
            </option>
          </select>
          <span className="cp-role-modal__help">{TIER_HELP[tier]}</span>
        </label>

        {errorMessage && (
          <div className="cp-role-modal__error" role="alert">
            <AlertCircle size={14} />
            <span>{errorMessage}</span>
          </div>
        )}

        <div className="cp-role-modal__actions">
          <button type="button" className="cp-role-modal__cancel" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="submit" className="cp-role-modal__submit" disabled={submitting}>
            {submitting && <Loader2 size={14} className="cp-role-modal__spinner" />}
            Save
          </button>
        </div>
      </form>
    </Modal>
  );
}
